import React, { useState } from 'react';
import { ArrowRight, ChevronDown, MessageCircle, Mail, Phone, MapPin } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { COMPANY } from '../mock';
import { Reveal, useSheen } from './Reveal';

const FaqContact = ({ faqs, serviceOptions }) => {
  const [openIdx, setOpenIdx] = useState(0);
  const { toast } = useToast();
  const [form, setForm] = useState({
    fullName: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const { onMouseMove } = useSheen();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.fullName || !form.company || !form.email || !form.service) {
      toast({
        title: 'Please complete required fields',
        description: 'Full name, company, email and service are required.',
      });
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      // Save locally for mock
      try {
        const existing = JSON.parse(localStorage.getItem('arvanta_inquiries') || '[]');
        existing.push({ ...form, ts: Date.now() });
        localStorage.setItem('arvanta_inquiries', JSON.stringify(existing));
      } catch (_) {}
      setSubmitting(false);
      toast({
        title: 'Inquiry submitted',
        description: "Thanks! We'll get back within 24 business hours.",
      });
      setForm({
        fullName: '',
        company: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });
    }, 700);
  };

  return (
    <section id="faq" className="relative py-24 lg:py-32 px-6 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="text-center mb-16">
          <div className="section-num mb-5 justify-center">
            <span className="num">07</span>
            <span>Enterprise FAQs</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            <span className="text-white">Questions Enterprise </span>
            <span className="text-gradient-mixed">Buyers Ask Us</span>
          </h2>
        </Reveal>

        <div id="contact" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* FAQs */}
          <Reveal variant="left" className="space-y-3">
            {faqs.map((f, i) => {
              const open = openIdx === i;
              return (
                <div
                  key={i}
                  onMouseMove={onMouseMove}
                  className={`card-sheen glass-card rounded-2xl overflow-hidden ${
                    open ? 'border-amber-500/30' : ''
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : i)}
                    className="w-full text-left p-6 flex items-start gap-5"
                  >
                    <span className="text-amber-400 font-bold text-xs tracking-widest mt-1">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-4">
                        <h3 className="text-base lg:text-lg font-semibold text-white">{f.q}</h3>
                        <ChevronDown
                          className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${
                            open ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                      <div
                        className={`grid transition-all duration-300 ${
                          open ? 'grid-rows-[1fr] mt-3 opacity-100' : 'grid-rows-[0fr] opacity-0'
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-sm text-slate-400 leading-relaxed">{f.a}</p>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}

            <div className="mt-6 space-y-3">
              <div className="glass-card rounded-2xl p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="font-semibold text-white">Need a tailored proposal?</div>
                  <div className="text-sm text-slate-400">
                    Procurement teams can reach us directly at{' '}
                    <a
                      href={`mailto:${COMPANY.email}`}
                      className="text-amber-400 hover:text-amber-300"
                    >
                      {COMPANY.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <a
                  href={`tel:${COMPANY.phoneHref}`}
                  className="glass-card glass-card-hover rounded-2xl p-5 flex items-center gap-4"
                >
                  <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400 font-semibold">
                      Call us
                    </div>
                    <div className="text-sm font-semibold text-white truncate">
                      {COMPANY.phone}
                    </div>
                  </div>
                </a>
                <div className="glass-card rounded-2xl p-5 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-blue-500/15 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] tracking-[0.2em] uppercase text-slate-400 font-semibold">
                      Visit
                    </div>
                    <div className="text-sm font-semibold text-white truncate">
                      {COMPANY.addressShort}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Quote form */}
          <Reveal variant="right">
            <div onMouseMove={onMouseMove} className="card-sheen glass-card rounded-3xl p-8 lg:p-10">
            <h3 className="text-2xl font-bold text-white mb-2">Get a Free Quote</h3>
            <p className="text-sm text-slate-400 mb-7">
              Tell us about your requirements and we'll get back within 24 hours.
            </p>

            <form onSubmit={onSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fullName" className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    value={form.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    placeholder="Rajesh Kumar"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-amber-400/40 focus-visible:border-amber-400/60"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                    Company *
                  </Label>
                  <Input
                    id="company"
                    value={form.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    placeholder="Your Company Ltd."
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-amber-400/40 focus-visible:border-amber-400/60"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="you@company.com"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-amber-400/40 focus-visible:border-amber-400/60"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+91 98765 43210"
                    className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-12 rounded-xl focus-visible:ring-amber-400/40 focus-visible:border-amber-400/60"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                  Service Required *
                </Label>
                <Select value={form.service} onValueChange={(v) => handleChange('service', v)}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 text-white h-12 rounded-xl focus:ring-amber-400/40">
                    <SelectValue placeholder="Select a service..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1729] border-white/10 text-white">
                    {serviceOptions.map((s) => (
                      <SelectItem key={s} value={s} className="focus:bg-white/5 focus:text-white">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-[11px] tracking-[0.2em] uppercase text-amber-400 font-semibold">
                  Message
                </Label>
                <Textarea
                  id="message"
                  rows={4}
                  value={form.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  placeholder="Describe your requirements, tender category, or digital goals..."
                  className="mt-2 bg-white/5 border-white/10 text-white placeholder:text-slate-500 rounded-xl focus-visible:ring-amber-400/40 focus-visible:border-amber-400/60"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 btn-amber rounded-full px-6 py-4 text-sm sm:text-base disabled:opacity-70"
              >
                {submitting ? 'Submitting...' : 'Submit Inquiry'}
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-center text-xs text-slate-400">
                We respond within 24 business hours. No spam, ever.
              </p>
            </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default FaqContact;
